import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
} from "@mui/material";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDBadge from "components/MDBadge";
import { ListLayout, TableCard } from "ui-components/Layout";
import { Error_, MessageDialog, LoadingDialog } from "ui-components/Alert";
import CheckboxTable2 from "ui-components/tables/CheckboxTable2";
import { CheckboxCell } from "ui-components/tables/components/table";
import { useCustomerValue } from "providers/CustomerContext";
import { getFulfillmentOrders, initFulfillmentAwb } from "services/data.outbound.service";

const EMPTY_ORDERS = [];
const MAX_SELECTION = 100;

function SelectUpToHeaderCell({ table, failedMap }) {
  const rows = table.getSortedRowModel().rows;
  const selectedCount = Object.keys(table.getState().rowSelection).length;
  const checked = selectedCount > 0;
  const indeterminate = selectedCount > 0 && selectedCount < rows.length;

  const onChange = () => {
    if (selectedCount > 0) {
      table.resetRowSelection();
      return;
    }
    // orders that failed earlier are skipped so they don't keep taking up the slots
    const capped = rows.filter((row) => !failedMap[row.original.orderId]).slice(0, MAX_SELECTION);
    const next = {};
    capped.forEach((row) => {
      next[row.id] = true;
    });
    table.setRowSelection(next);
  };

  return (
    <Tooltip title={`Select up to ${MAX_SELECTION} orders`}>
      <Checkbox
        sx={{ padding: 0, "& .MuiSvgIcon-root": { borderColor: "#1A73E8" } }}
        checked={checked}
        indeterminate={indeterminate}
        onChange={onChange}
      />
    </Tooltip>
  );
}

function FulfillmentOrder() {
  const { customerCode } = useCustomerValue();
  const queryClient = useQueryClient();
  const [alert, setAlert] = useState(null);
  const [selectedRows, setSelectedRows] = useState([]);
  const [rowSelection, setRowSelection] = useState({});
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [results, setResults] = useState(null);
  // orderId -> last failure message, kept in component state so it survives the list refetch
  const [failedMap, setFailedMap] = useState({});

  const onError = (data) => setAlert(<Error_ data={data} onChange={setAlert} />);

  const { data, isFetching } = useQuery({
    queryKey: ["fulfillment-orders", { customerCode }],
    queryFn: () => getFulfillmentOrders({ customerCode }),
    enabled: Boolean(customerCode),
    select: (res) => res.data.data.map((x) => ({ ...x, id: x.orderId })),
    onError,
  });

  const initMutation = useMutation(initFulfillmentAwb, {
    onSuccess: (res) => {
      const list = res.data.data.map((r) => ({
        ...r,
        custOrdNbr: (data ?? []).find((o) => o.orderId === r.orderId)?.custOrdNbr ?? r.orderId,
      }));
      setResults(list);
      setFailedMap((prev) => {
        const next = { ...prev };
        list.forEach((r) => {
          if (r.success) delete next[r.orderId];
          else next[r.orderId] = r.message || "Failed";
        });
        return next;
      });
      setRowSelection({});
      queryClient.invalidateQueries(["fulfillment-orders"]);
    },
    onError,
  });

  const columns = [
    { accessorKey: "marketplace", header: "Marketplace" },
    { accessorKey: "custOrdNbr", header: "Cust Order Number" },
    { accessorKey: "orderDate", header: "Order Date" },
    { accessorKey: "status", header: "Status" },
    {
      id: "lastResult",
      header: "Last Result",
      enableSorting: false,
      cell: ({ row }) => {
        const msg = failedMap[row.original.orderId];
        return msg ? <span style={{ color: "#F44335", wordBreak: "break-word" }}>{msg}</span> : null;
      },
    },
  ];

  const columnsWithSelect = [
    {
      id: "select",
      header: (props) => <SelectUpToHeaderCell {...props} failedMap={failedMap} />,
      cell: CheckboxCell,
    },
    ...columns,
  ];

  // failed orders sink to the bottom (stable, so date order is kept within each group)
  const tableData = useMemo(() => {
    const list = data ?? EMPTY_ORDERS;
    if (Object.keys(failedMap).length === 0) return list;
    return [...list.filter((o) => !failedMap[o.orderId]), ...list.filter((o) => failedMap[o.orderId])];
  }, [data, failedMap]);

  const isLoading = isFetching || initMutation.status === "loading";

  return (
    <ListLayout>
      {isLoading && <LoadingDialog />}
      {confirmOpen && (
        <MessageDialog
          title={`Init AWB for ${selectedRows.length} order(s)?`}
          onClose={() => setConfirmOpen(false)}
          onOk={() => {
            setConfirmOpen(false);
            initMutation.mutate({ customerCode, orderIds: selectedRows.map((x) => x.orderId) });
          }}
        />
      )}
      {results && <ResultDialog results={results} onClose={() => setResults(null)} />}
      <MDBox mt={3} mb={1}>
        {alert}
      </MDBox>
      <TableCard sx={{ mt: 1 }}>
        <MDBox display="flex" justifyContent="space-between" alignItems="center" margin="10px">
          <MDButton
            variant="gradient"
            color="info"
            onClick={() => setConfirmOpen(true)}
            disabled={selectedRows.length === 0 || isLoading}
          >
            init awb ({selectedRows.length})
          </MDButton>
        </MDBox>
        <CheckboxTable2
          data={tableData}
          columns={columnsWithSelect}
          onSelectedRowsChange={setSelectedRows}
          customRowSelection={rowSelection}
          setCustomRowSelection={setRowSelection}
        />
      </TableCard>
    </ListLayout>
  );
}

const ResultDialog = ({ results, onClose }) => {
  const successCount = results.filter((r) => r.success).length;
  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        Success {successCount} / {results.length} order(s)
      </DialogTitle>
      <DialogContent>
        <Table size="small" sx={{ tableLayout: "fixed" }}>
          <TableHead sx={{ display: "table-header-group" }}>
            <TableRow>
              <TableCell sx={{ width: "30%" }}>Cust Order Number</TableCell>
              <TableCell sx={{ width: "20%" }}>Result</TableCell>
              <TableCell sx={{ width: "50%" }}>Message</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {results.map((r) => (
              <TableRow key={r.orderId}>
                <TableCell sx={{ width: "30%", wordBreak: "break-word" }}>{r.custOrdNbr}</TableCell>
                <TableCell sx={{ width: "20%" }}>
                  <MDBadge
                    variant="gradient"
                    color={r.success ? "success" : "error"}
                    size="xs"
                    badgeContent={r.success ? "Success" : "Failed"}
                    container
                  />
                </TableCell>
                <TableCell sx={{ width: "50%", wordBreak: "break-word" }}>{r.message}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </DialogContent>
      <DialogActions style={{ display: "flex", justifyContent: "center" }}>
        <MDButton variant="outlined" color="info" onClick={onClose}>
          Close
        </MDButton>
      </DialogActions>
    </Dialog>
  );
};

export default FulfillmentOrder;
